import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { message } from 'antd';
import { leaguesApi } from '@/api/leagues.api';
import type { LeagueListParams, LeagueCreateRequest, LeagueUpdateRequest } from '@/types/league';

export const LEAGUE_QUERY_KEYS = {
  all: ['leagues'] as const,
  list: (params?: LeagueListParams) => ['leagues', 'list', params] as const,
  detail: (id: string) => ['leagues', 'detail', id] as const,
};

export function useLeagueList(params?: LeagueListParams) {
  return useQuery({
    queryKey: LEAGUE_QUERY_KEYS.list(params),
    queryFn: () => leaguesApi.getList(params),
  });
}

export function useLeagueDetail(id: string) {
  return useQuery({
    queryKey: LEAGUE_QUERY_KEYS.detail(id),
    queryFn: () => leaguesApi.getDetail(id),
    enabled: !!id,
  });
}

export function useCreateLeague() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: LeagueCreateRequest) => leaguesApi.create(data),
    onSuccess: () => {
      message.success('리그가 생성되었습니다.');
      queryClient.invalidateQueries({ queryKey: LEAGUE_QUERY_KEYS.all });
    },
    onError: () => {
      message.error('리그 생성에 실패했습니다.');
    },
  });
}

export function useUpdateLeague() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: LeagueUpdateRequest }) =>
      leaguesApi.update(id, data),
    onSuccess: (_, { id }) => {
      message.success('리그가 수정되었습니다.');
      queryClient.invalidateQueries({ queryKey: LEAGUE_QUERY_KEYS.detail(id) });
      queryClient.invalidateQueries({ queryKey: LEAGUE_QUERY_KEYS.all });
    },
    onError: () => {
      message.error('리그 수정에 실패했습니다.');
    },
  });
}

export function usePublishLeague() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => leaguesApi.publish(id),
    onSuccess: () => {
      message.success('리그가 공개(SCHEDULED)로 변경되었습니다.');
      queryClient.invalidateQueries({ queryKey: LEAGUE_QUERY_KEYS.all });
    },
    onError: () => {
      message.error('리그 공개에 실패했습니다.');
    },
  });
}

export function useStartLeague() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => leaguesApi.start(id),
    onSuccess: () => {
      message.success('리그가 시작되었습니다.');
      queryClient.invalidateQueries({ queryKey: LEAGUE_QUERY_KEYS.all });
    },
    onError: () => {
      message.error('리그 시작에 실패했습니다.');
    },
  });
}

export function useEndLeague() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => leaguesApi.end(id),
    onSuccess: () => {
      message.success('리그가 종료되었습니다.');
      queryClient.invalidateQueries({ queryKey: LEAGUE_QUERY_KEYS.all });
    },
    onError: () => {
      message.error('리그 종료에 실패했습니다.');
    },
  });
}

export function useDeleteLeague() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => leaguesApi.remove(id),
    onSuccess: () => {
      message.success('리그가 삭제되었습니다.');
      queryClient.invalidateQueries({ queryKey: LEAGUE_QUERY_KEYS.all });
    },
    onError: () => {
      message.error('리그 삭제에 실패했습니다.');
    },
  });
}
